/**
 * Miroir de `backend/app/core/station_annex_calc.py` pour l'aperçu d'une vente
 * annexe (gaz, lubrifiants, boutique, prestations).
 *
 * Le serveur reste la seule source des montants enregistrés ; ce fichier donne
 * le même résultat avant l'envoi : montant au franc (la moitié s'éloigne de
 * zéro), consigne des bouteilles dont le vide n'est pas rendu, remboursement
 * des vides rendus seuls. Calcul en entiers (millièmes d'unité), jamais en
 * virgule flottante sur le montant.
 *
 * Mêmes jeux de valeurs que `backend/app/tests/unit/test_station_annexes.py`
 * (`stationAnnexCalc.test.js`). Si l'un change, l'autre aussi.
 */
import { StationCalcError } from './stationCalc';

const MILLIEMES = 1000;
const QUANTITE_MAX = 100000;

const nombre = (valeur, quoi) => {
  if (valeur === null || valeur === undefined || valeur === '' || typeof valeur === 'boolean') {
    throw new StationCalcError(`${quoi} manquant.`);
  }
  const brut = typeof valeur === 'number' ? valeur : Number(String(valeur).trim().replace(/[\s  ]/g, '').replace(',', '.'));
  if (!Number.isFinite(brut)) throw new StationCalcError(`${quoi} invalide : ${valeur}.`);
  return brut;
};

/** Millièmes d'unité entiers, la moitié s'éloignant de zéro. */
const enMilliemes = (valeur, quoi) => {
  const brut = nombre(valeur, quoi) * MILLIEMES;
  return Math.sign(brut) * Math.round(Math.abs(brut));
};

/** Quantité au millième. */
export const arrondiQuantite = (valeur, quoi = 'Quantité') => enMilliemes(valeur, quoi) / MILLIEMES;

const controleQuantite = (quantite, entier, quoi) => {
  if (quantite > QUANTITE_MAX) throw new StationCalcError(`${quoi} trop grande.`);
  if (entier && !Number.isInteger(quantite)) throw new StationCalcError(`${quoi} : indiquez un nombre entier.`);
  return quantite;
};

/** Quantité strictement positive ; entière pour une pièce ou une bouteille. */
export const quantitePositive = (valeur, { entier = false, quoi = 'Quantité' } = {}) => {
  const quantite = arrondiQuantite(valeur, quoi);
  if (quantite <= 0) throw new StationCalcError(`${quoi} : indiquez un nombre supérieur à 0.`);
  return controleQuantite(quantite, entier, quoi);
};

/** Quantité positive ou nulle (vides rendus) ; vide = 0. */
export const quantiteNonNegative = (valeur, { entier = false, quoi = 'Quantité' } = {}) => {
  if (valeur === null || valeur === undefined || valeur === '') return 0;
  const quantite = arrondiQuantite(valeur, quoi);
  if (quantite < 0) throw new StationCalcError(`${quoi} : le nombre ne peut pas être négatif.`);
  return controleQuantite(quantite, entier, quoi);
};

/** Quantité × prix, arrondi au franc (la moitié s'éloigne de zéro). */
export const montantLigne = (quantite, prix) => {
  const milliemes = enMilliemes(quantite, 'Quantité');
  const unitaire = nombre(prix, 'Prix');
  const brut = milliemes * unitaire;
  return Math.sign(brut) * Math.floor((Math.abs(brut) + MILLIEMES / 2) / MILLIEMES);
};

/**
 * Consigne encaissée : une par bouteille vendue dont le vide n'est pas rendu.
 * Plus de vides rendus que de bouteilles vendues n'est pas un échange.
 */
export const consigneEncaissee = (quantite, videsRendus, consigne) => {
  const vendues = nombre(quantite, 'Quantité');
  const rendus = nombre(videsRendus, 'Vides rendus');
  if (rendus < 0) throw new StationCalcError('Vides rendus : le nombre ne peut pas être négatif.');
  if (rendus > vendues) {
    throw new StationCalcError('Il ne peut pas y avoir plus de vides rendus que de bouteilles vendues.');
  }
  return montantLigne(vendues - rendus, consigne);
};

/** Consigne remboursée pour des vides rendus sans achat. */
export const consigneRemboursee = (vides, consigne) => {
  const nombreDeVides = nombre(vides, 'Vides rendus');
  if (nombreDeVides <= 0) throw new StationCalcError('Vides rendus : indiquez un nombre supérieur à 0.');
  return montantLigne(nombreDeVides, consigne);
};

/**
 * Aperçu d'une vente : effet sur le stock, montant des articles, consigne,
 * total à encaisser. Bouteille échangée : `quantity` −1, `empties` +1.
 */
export const apercuVente = ({ quantite, prix, videsRendus = 0, consigne = 0, consignee = false }) => {
  const vendues = nombre(quantite, 'Quantité');
  const rendus = consignee ? nombre(videsRendus, 'Vides rendus') : 0;
  const depot = consignee ? consigneEncaissee(vendues, rendus, consigne) : 0;
  const montant = montantLigne(vendues, prix);
  return {
    quantity: -vendues,
    empties: rendus,
    amount: montant,
    depositAmount: depot,
    total: montant + depot,
  };
};

/** Aperçu de vides rendus seuls : vides +n, consigne remboursée (négative). */
export const apercuRetourVides = ({ vides, consigne }) => {
  const rembourse = consigneRemboursee(vides, consigne);
  return { quantity: 0, empties: nombre(vides, 'Vides rendus'), amount: 0, depositAmount: -rembourse, total: -rembourse };
};

/** `"FULL"` ou `"EMPTY"` si le mouvement ferait passer ce stock sous zéro, sinon `null`. */
export const stockInsuffisant = (pleines, vides, effetPleines, effetVides) => {
  if (enMilliemes(pleines, 'Stock') + enMilliemes(effetPleines, 'Quantité') < 0) return 'FULL';
  if (enMilliemes(vides, 'Stock') + enMilliemes(effetVides, 'Vides') < 0) return 'EMPTY';
  return null;
};

/** Bouteilles consignées chez les clients : vendues − vides rendus (échange ou seuls), jamais sous zéro à l'écran. */
export const bouteillesChezLesClients = (vendues, rendusEnEchange, rendusSeuls) =>
  Math.max(0, nombre(vendues, 'Vendues') - nombre(rendusEnEchange, 'Vides rendus') - nombre(rendusSeuls, 'Vides rendus'));
